import { ComponentProps } from "react";
import { Field, FieldDescription, FieldLabel } from "../ui/field";
import { DebounceProps, FieldErrors, FieldProps } from "./field";
import { DecimalInput, DecimalInputDebounced } from "../inputs/decimal-input";
import { Input } from "../ui/input";
import { InputDebounced } from "../inputs/input-debounced";

/**
 * InputField provices a single component
 * wrapping shadcn/ui Field components
 * to collect input.
 */
export function InputField({
  label,
  description,
  errors,
  fieldProps,
  debounce,
  delay,
  ...props
}: ComponentProps<"input"> & FieldProps & DebounceProps) {
  return (
    <Field {...fieldProps}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <InputDebounceSwitch
        aria-invalid={errors && errors.length > 0}
        debounce={debounce}
        delay={delay}
        {...props}
      />
      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldErrors errors={errors} />
    </Field>
  );
}

/**
 * TextInputField provices a single component
 * wrapping shadcn/ui Field components
 * to collect input.
 *
 * It is scoped to text input and frees the
 * caller from calling e.target.value.
 */
export function TextInputField({
  label,
  description,
  errors,
  fieldProps,
  text,
  onTextChange,
  debounce,
  delay,
  ...props
}: {
  text: string | undefined;
  onTextChange: (text: string) => void;
} & Omit<ComponentProps<"input">, "type" | "value" | "onChange"> &
  FieldProps &
  DebounceProps) {
  return (
    <Field {...fieldProps}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <InputDebounceSwitch
        aria-invalid={errors && errors.length > 0}
        debounce={debounce}
        delay={delay}
        {...props}
        type="text"
        value={text}
        onChange={(e) => onTextChange(e.target.value)}
      />
      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldErrors errors={errors} />
    </Field>
  );
}

/**
 * NumberInputField provices a single component
 * wrapping shadcn/ui Field components
 * to collect input.
 *
 * It is scoped to number input and frees the
 * caller from calling e.target.value.
 */
export function NumberInputField({
  label,
  description,
  errors,
  fieldProps,
  number,
  onNumberChange,
  debounce,
  delay,
  ...props
}: {
  number: number | undefined;
  onNumberChange: (number: number) => void;
} & Omit<ComponentProps<"input">, "type" | "value" | "onChange"> &
  FieldProps &
  DebounceProps) {
  return (
    <Field {...fieldProps}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <InputDebounceSwitch
        aria-invalid={errors && errors.length > 0}
        debounce={debounce}
        delay={delay}
        {...props}
        type="number"
        value={number}
        onChange={(e) => onNumberChange(Number(e.target.value))}
      />
      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldErrors errors={errors} />
    </Field>
  );
}

/**
 * InputDebounceSwitch renders the input of a field, debounced by default.
 *
 * Switching `debounce` swaps the component and therefore remounts the input,
 * discarding its buffer — it is meant as a per-call-site decision, not
 * something to toggle while the field is on screen.
 */
function InputDebounceSwitch({
  debounce = true,
  delay,
  ...props
}: ComponentProps<"input"> & DebounceProps) {
  if (!debounce) {
    return <Input {...props} />;
  }

  return <InputDebounced delay={delay} {...props} />;
}

/**
 * DecimalNumberInputField provices a single component
 * wrapping shadcn/ui Field components
 * to collect input.
 *
 * See {@link DecimalInput} documentation for more information.
 */
export function DecimalNumberInputField({
  label,
  description,
  errors,
  fieldProps,
  debounce,
  delay,
  ...props
}: ComponentProps<typeof DecimalInput> & FieldProps & DebounceProps) {
  return (
    <Field {...fieldProps}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <DecimalInputDebounceSwitch
        aria-invalid={errors && errors.length > 0}
        debounce={debounce}
        delay={delay}
        {...props}
      />
      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldErrors errors={errors} />
    </Field>
  );
}

/**
 * DecimalInputDebounceSwitch renders the decimal input of a field, debounced by
 * default. See {@link InputDebounceSwitch} for the `debounce` semantics.
 */
function DecimalInputDebounceSwitch({
  debounce = true,
  delay,
  ...props
}: ComponentProps<typeof DecimalInput> & DebounceProps) {
  if (!debounce) {
    return <DecimalInput {...props} />;
  }

  return <DecimalInputDebounced delay={delay} {...props} />;
}
